import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import styled from "styled-components";
import axios from "axios";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import API_ENDPOINTS from "../config/api";

const Container = styled.main`
  min-height: 100dvh;
  width: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: #013566;
  margin: 0 0 1rem 0;
  text-align: center;
`;

const GoBack = styled.p`
  font-size: 1.4rem;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
  color: #013566;
  margin-top: 1rem;
  margin-bottom: 2rem;
`;

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  width: 100%;
`;

const FormTile = styled.div`
  border: 2px solid #eee;
  border-radius: 12px;
  -webkit-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  -moz-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 2rem;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  font-size: 1rem;
  font-weight: 600;
  color: #013566;
`;

const Input = styled.input`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  color: #333;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }

  &::placeholder {
    color: #6c757d;
  }
`;

const QuillWrapper = styled.div`
  .ql-container {
    border-radius: 0 0 8px 8px;
    border: 1px solid #ddd;
    border-top: none;
    font-size: 1rem;
    min-height: 200px;
  }

  .ql-toolbar {
    border-radius: 8px 8px 0 0;
    border: 1px solid #ddd;
    background: #f8f9fa;
  }

  .ql-toolbar:focus-within,
  .ql-container:focus-within {
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }

  .ql-editor {
    min-height: 200px;
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 1rem;
  background: white;
  color: #333;
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }
`;

const LogoPreview = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
`;

const LogoImage = styled.img`
  width: 60px;
  height: 60px;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid #ddd;
  padding: 4px;
  background: white;
`;

const PreviewText = styled.p`
  font-size: 0.9rem;
  color: #6c757d;
  font-style: italic;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
`;

const Button = styled.button<{ $variant?: "primary" | "secondary" }>`
  padding: 0.75rem 2rem;
  border: ${(props) =>
    props.$variant === "secondary" ? "1px solid #013566" : "none"};
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${(props) =>
    props.$variant === "secondary" ? "transparent" : "#013566"};
  color: ${(props) => (props.$variant === "secondary" ? "#013566" : "white")};

  &:hover:not(:disabled) {
    background-color: ${(props) =>
      props.$variant === "secondary" ? "#f8f9fa" : "#012a4a"};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

interface Props {
  setSnackbar: any;
}

const EditClient: React.FC<Props> = ({ setSnackbar }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [clientName, setClientName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [phone, setPhone] = useState("");
  const [logo, setLogo] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>("");
  const [status, setStatus] = useState("Active");
  const [description, setDescription] = useState("");
  const [tagline, setTagline] = useState("");
  const [paidBy, setPaidBy] = useState("");
  const [amount, setAmount] = useState("");
  const [trafficPricing, setTrafficPricing] = useState<Record<string, string>>({
    email: "",
    social: "",
    sms: "",
    listicle: "",
    search: "",
    host_and_post: "",
  });
  const [verticalId, setVerticalId] = useState("");
  const [verticals, setVerticals] = useState<any[]>([]);
  const [loadingVerticals, setLoadingVerticals] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const token = localStorage.getItem("token");

  const formatCurrencyInput = (value: string) => {
    const cleaned = value.replace(/[^0-9.]/g, "");
    const [intPart, decPart] = cleaned.split(".");
    const intSafe = intPart ?? "";
    const decSafe = decPart ? decPart.slice(0, 2) : "";
    return decPart !== undefined ? `${intSafe}.${decSafe}` : intSafe;
  };

  const formatPercentInput = (value: string) => {
    const cleaned = value.replace(/[^0-9.]/g, "");
    const [intPart, decPart] = cleaned.split(".");
    const intSafe = intPart ?? "";
    const decSafe = decPart ? decPart.slice(0, 2) : "";
    return decPart !== undefined ? `${intSafe}.${decSafe}` : intSafe;
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      [{ size: ["small", false, "large", "huge"] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ script: "sub" }, { script: "super" }],
      [{ align: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      ["blockquote", "code-block"],
      ["link"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "script",
    "align",
    "list",
    "indent",
    "blockquote",
    "code-block",
    "link",
  ];

  // Fetch verticals
  useEffect(() => {
    const fetchVerticals = async () => {
      try {
        setLoadingVerticals(true);
        const response = await axios.get(API_ENDPOINTS.VERTICALS, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setVerticals(response.data || []);
      } catch (error) {
        console.error("Error fetching verticals:", error);
      } finally {
        setLoadingVerticals(false);
      }
    };

    fetchVerticals();
  }, [token]);

  useEffect(() => {
    const loadClientData = async () => {
      // Fallback: fetch client data from API
      if (!id) {
        setError("Client ID is missing");
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(API_ENDPOINTS.CLIENT_DETAILS(id), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        console.log(response.data);
        const client = response.data;
        if (client) {
          setClientName(client.name || "");
          setLegalName(client.legal_name || "");
          setPhone(client.phone || "");
          setLogo(client.logo_url || "");
          setLogoPreview(client.logo_url || "");
          setStatus(client.status === 1 ? "Active" : "Inactive");
          setDescription(client.description || "");
          setTagline(client.tagline || "");
          const rawSettlement = (client.settlement_type ?? "").toString();
          const normalizedSettlement = rawSettlement.trim().toUpperCase();
          const paidByValue =
            normalizedSettlement === "COM" ? "CPM" : normalizedSettlement;
          setPaidBy(paidByValue);
          setAmount(client.amount ? client.amount.toString() : "");
          if (Array.isArray(client.traffic_pricing)) {
            const next: Record<string, string> = {
              email: "",
              social: "",
              sms: "",
              listicle: "",
              search: "",
              host_and_post: "",
            };
            for (const tp of client.traffic_pricing) {
              if (tp?.traffic_type && tp?.amount !== undefined && tp?.amount !== null) {
                const t = String(tp.traffic_type);
                if (t in next) {
                  next[t] = String(tp.amount);
                }
              }
            }
            setTrafficPricing(next);
          }
          // Set current vertical if client has one
          if (client.vertical_id) {
            setVerticalId(client.vertical_id.toString());
          }
        }
      } catch (error) {
        console.error("Error fetching client data:", error);
        setError("Failed to load client data");
      } finally {
        setLoading(false);
      }
    };

    loadClientData();
  }, [id, location.state, token]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    if (!id) {
      setError("Client ID is missing");
      return;
    }

    if (!paidBy) {
      setError("Paid By is required");
      setSaving(false);
      return;
    }

    if (paidBy === "CPL") {
      const entries = Object.entries(trafficPricing)
        .map(([traffic_type, v]) => ({
          traffic_type,
          amount: v.trim(),
        }))
        .filter((x) => x.amount.length > 0);

      if (entries.length === 0) {
        setError("Please enter at least one traffic pricing amount");
        setSaving(false);
        return;
      }
    } else {
      if (!amount.trim()) {
        setError("Amount is required");
        setSaving(false);
        return;
      }
    }

    try {
      const formData = new FormData();
      formData.append("name", clientName);
      formData.append("legal_name", legalName);
      formData.append("phone", phone.trim());
      // Convert status to 1 for Active, 0 for Inactive
      formData.append("status", status === "Active" ? "1" : "0");
      formData.append("description", description);
      formData.append("tagline", tagline);
      if (verticalId) {
        formData.append("vertical_id", verticalId);
      }
      formData.append("settlement_type", paidBy.toLowerCase());

      if (paidBy === "CPL") {
        const traffic_pricing = Object.entries(trafficPricing)
          .map(([traffic_type, v]) => ({
            traffic_type,
            amount: Number(v.trim()),
          }))
          .filter((x) => Number.isFinite(x.amount));
        traffic_pricing.forEach((tp, idx) => {
          formData.append(`traffic_pricing[${idx}][traffic_type]`, tp.traffic_type);
          formData.append(`traffic_pricing[${idx}][amount]`, tp.amount.toFixed(2));
        });
      } else {
        formData.append("amount", amount.trim());
      }

      // Only append logo if a new file was selected
      if (logoFile) {
        formData.append("logo", logoFile);
      }

      await axios.put(API_ENDPOINTS.CLIENT_DETAILS(id), formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      setSnackbar({
        isOpen: true,
        message: "Client updated successfully",
        type: "success",
      });
    } catch (err: any) {
      console.error("Error updating client:", err);
      setError(
        err.response?.data?.message ||
          "Failed to update client. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/clients");
  };

  if (loading) {
    return (
      <Container>
        <PageTitle>Edit Client</PageTitle>
        <GoBack onClick={() => navigate("/clients")}>← Back to Clients</GoBack>
        <FormContainer>
          <FormTile>
            <p>Loading client data...</p>
          </FormTile>
        </FormContainer>
      </Container>
    );
  }

  return (
    <Container>
      <PageTitle>Edit Client</PageTitle>
      <GoBack onClick={() => navigate("/clients")}>← Back to Clients</GoBack>

      <FormContainer>
        <FormTile>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="clientName">Client Name</Label>
              <Input
                type="text"
                id="clientName"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Enter client name"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="legalName">Legal Name</Label>
              <Input
                type="text"
                id="legalName"
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                placeholder="Enter legal name"
                required
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                type="tel"
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter phone number"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="vertical">Vertical (optional)</Label>
              <Select
                id="vertical"
                value={verticalId}
                onChange={(e) => setVerticalId(e.target.value)}
                disabled={loadingVerticals}
              >
                <option value="">
                  {loadingVerticals
                    ? "Loading verticals..."
                    : "Select a vertical"}
                </option>
                {verticals.map((vertical) => (
                  <option key={vertical.id} value={vertical.id}>
                    {vertical.name
                      ? vertical.name.charAt(0).toUpperCase() +
                        vertical.name.slice(1)
                      : `Vertical ${vertical.id}`}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="tagline">Tagline</Label>
              <Input
                type="text"
                id="tagline"
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                placeholder="Enter tagline"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="description">Description</Label>
              <QuillWrapper>
                <ReactQuill
                  theme="snow"
                  value={description}
                  onChange={setDescription}
                  placeholder="Enter description"
                  modules={quillModules}
                  formats={quillFormats}
                />
              </QuillWrapper>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="logo">Logo</Label>
              <Input
                type="file"
                id="logo"
                accept="image/*"
                onChange={handleLogoChange}
              />
              {(logoPreview || logo) && (
                <LogoPreview>
                  <LogoImage src={logoPreview || logo} alt="Logo preview" />
                  <PreviewText>
                    {logoFile ? "New Logo Preview" : "Current Logo"}
                  </PreviewText>
                </LogoPreview>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="status">Status</Label>
              <Select
                id="status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                required
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="paidBy">Paid By *</Label>
              <Select
                id="paidBy"
                value={paidBy}
                onChange={(e) => {
                  setPaidBy(e.target.value);
                  setAmount("");
                  setTrafficPricing({
                    email: "",
                    social: "",
                    sms: "",
                    listicle: "",
                    search: "",
                    host_and_post: "",
                  });
                }}
                required
              >
                <option disabled value="">
                  Select one
                </option>
                <option value="CPL">CPL</option>
                <option value="CPM">COM</option>
                <option value="APPT">APPT</option>
              </Select>
            </FormGroup>

            {paidBy === "CPL" && (
              <FormGroup>
                <Label>Traffic Pricing</Label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(240px, 1fr))",
                    gap: "12px 16px",
                  }}
                >
                  {(
                    [
                      ["email", "Email"],
                      ["social", "Social"],
                      ["sms", "SMS"],
                      ["listicle", "Listicle"],
                      ["search", "Search"],
                      ["host_and_post", "Host & Post"],
                    ] as const
                  ).map(([key, label]) => (
                    <div key={key} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      <Label htmlFor={`traffic-${key}`}>{label}</Label>
                      <Input
                        type="text"
                        inputMode="decimal"
                        id={`traffic-${key}`}
                        value={trafficPricing[key] ? `$${trafficPricing[key]}` : ""}
                        onChange={(e) => {
                          const next = e.target.value.replace(/[$]/g, "");
                          setTrafficPricing((prev) => ({
                            ...prev,
                            [key]: formatCurrencyInput(next),
                          }));
                        }}
                        placeholder="$0.00"
                      />
                    </div>
                  ))}
                </div>
              </FormGroup>
            )}

            {paidBy && paidBy !== "CPL" && (
              <FormGroup>
                <Label htmlFor="amount">
                  {paidBy === "CPM" ? "Amount (%) *" : "Amount *"}
                </Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  id="amount"
                  value={
                    amount
                      ? paidBy === "CPM"
                        ? `${amount}%`
                        : `$${amount}`
                      : ""
                  }
                  onChange={(e) => {
                    const raw = e.target.value;
                    const next = raw.replace(/[$%]/g, "");
                    setAmount(
                      paidBy === "CPM"
                        ? formatPercentInput(next)
                        : formatCurrencyInput(next),
                    );
                  }}
                  placeholder={paidBy === "CPM" ? "0.00%" : "$0.00"}
                  required
                />
              </FormGroup>
            )}

            {error && (
              <div style={{ color: "#d32f2f", marginBottom: "1rem" }}>
                {error}
              </div>
            )}

            <ButtonGroup>
              <Button
                type="button"
                $variant="secondary"
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </ButtonGroup>
          </form>
        </FormTile>
      </FormContainer>
    </Container>
  );
};

export default EditClient;
